import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Input } from "../../../foundation/altzero_base/base/components/ui/input";
import { Label } from "../../../foundation/altzero_base/base/components/ui/label";
import { Button } from "../../../foundation/altzero_base/base/components/ui/button";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../foundation/altzero_base/base/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "../../../foundation/altzero_base/base/components/ui/command";
import {
  User,
  Building2,
  FileText,
  Check,
  Mail,
  Phone,
  Save,
  Cloud,
  Loader2,
  AlertCircle,
  AlertTriangle,
  ChevronsUpDown,
} from "lucide-react";
import { useToast } from "../../../foundation/altzero_base/base/hooks/use-toast";
import { getUserOrganizationId } from "../services/tenderApiService";
import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";
import { usePermissions } from "../../../foundation/altzero_base/base/contextapi/PermissionsContext";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../foundation/altzero_base/base/components/ui/card";
import { Badge } from "../../../foundation/altzero_base/base/components/ui/badge";
import { WorkflowNavigation } from "./WorkflowNavigation";
import { useDocumentForm } from "../contexts/DocumentFormContext";
import SitesManagementSection from "./SitesManagementSection";
import { validateSite, createDefaultSite } from "../types/siteTypes";
import { createClient, updateClient } from "../services/clientService";
import { createSite } from "../services/siteService";
import { updateTender, syncTenderSites } from "../services/tenderService";
import { salesforceImportService } from "../services/salesforceImportService";
import { getTenderConsultant, saveTenderConsultant, getTenderPreparedFor, saveTenderPreparedFor, type TenderConsultant } from "../services/tenderWorkflowService";
import { mergeSalesforceConsultant } from "../lib/consultantFromSalesforce";
import { nmiBase } from "../lib/nmiMatch";
import { hasCompanyIdentifier, companyIdentifierError, hasAbnShape, hasAcnShape } from "../lib/companyIdentifier";
import type { SalesforceImportPayload, FieldMappingEntry, NormalizedSalesforceImport } from "../types/salesforceTypes";

// Client interface matching choiceenergy.client table
interface Client {
  id: string;
  org_id: string;
  name: string;
  abn: string | null;
  channel_partner: string | null;
  sf_account_id: string | null;
  extn: Record<string, any>;
  created_at: string;
}

interface StepProps {
  onNext: () => void;
  onBack: () => void;
}

const ClientInfoStep: React.FC<StepProps> = () => {
  const {
    clientData,
    setClientData,
    sites,
    setSites,
    handleNext: contextHandleNext,
    tenderName,
    setTenderName,
    selectedClientId,
    setSelectedClientId,
    createTenderWorkflow,
    saveCurrentStepData,
    tenderId,
    setTenderDetails,
    setSfOpportunityId,
    setSfTenderId,
    sfImportExists,
    setSfImportExists,
    sfImportedAt,
    setSfImportedAt,
  } = useDocumentForm();
  const [isNewClient, setIsNewClient] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showSiteErrors, setShowSiteErrors] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(true);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [clientComboOpen, setClientComboOpen] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const { toast } = useToast();
  const { can } = usePermissions();

  // Salesforce import local state
  const [sfOpportunityIdInput, setSfOpportunityIdInput] = useState('');
  const [sfTenderIdInput, setSfTenderIdInput] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [showReimportDialog, setShowReimportDialog] = useState(false);
  // Holds SF import data when tender doesn't exist yet at import time — stored to DB after tender creation
  const [pendingSfImport, setPendingSfImport] = useState<{
    sfOpportunityId: string;
    sfTenderId: string | null;
    rawPayload: SalesforceImportPayload;
    fieldMappingLog: FieldMappingEntry[];
    consultant: NormalizedSalesforceImport['consultant'];
  } | null>(null);

  // Fetch user's organization ID
  useEffect(() => {
    const fetchOrgId = async () => {
      try {
        const id = await getUserOrganizationId();
        setOrgId(id);
      } catch (error) {
        console.error("Error fetching org ID:", error);
        toast({
          title: "Error",
          description: "Could not load your organization",
          variant: "destructive",
        });
      }
    };
    fetchOrgId();
  }, [toast]);

  // Check if this tender already has a Salesforce import on mount
  useEffect(() => {
    if (!tenderId) return;
    salesforceImportService.checkImportExists(tenderId).then(({ exists, importedAt, sfOpportunityId }) => {
      if (exists) {
        setSfImportExists(true);
        setSfImportedAt(importedAt);
        setSfOpportunityIdInput(sfOpportunityId ?? '');
      }
    });
  }, [tenderId]);

  // Load clients function
  const loadClients = async () => {
    if (!orgId) return;

    setIsLoadingClients(true);
    try {
      const { data, error } = await supabase
        .from("client")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast({
        title: "Error",
        description: "Could not load clients",
        variant: "destructive",
      });
    } finally {
      setIsLoadingClients(false);
    }
  };

  // Fetch real clients from database
  useEffect(() => {
    loadClients();
  }, [orgId, toast]);

  // Initialize sites if empty
  useEffect(() => {
    if (sites.length === 0) {
      setSites([createDefaultSite()]);
    }
  }, [sites.length, setSites]);

  // Check if we have a valid session for data restoration
  const hasValidSession = () => {
    const sessionId = sessionStorage.getItem("scopingai_session_id");
    const sessionTimestamp = sessionStorage.getItem("scopingai_session_timestamp");

    if (!sessionId || !sessionTimestamp) return false;

    // Check if session is not too old (1 hour max)
    const now = Date.now();
    const sessionAge = now - parseInt(sessionTimestamp);
    const maxSessionAge = 60 * 60 * 1000; // 1 hour

    return sessionAge <= maxSessionAge;
  };

  // Sync clientFormData from context clientData (email/phone) — no sessionStorage restoration
  useEffect(() => {
    if (clientData.email || clientData.phone) {
      setClientFormData(prev => ({
        ...prev,
        email: clientData.email || prev.email,
        phone: clientData.phone || prev.phone,
      }));
    }
  }, [clientData.email, clientData.phone]);

  // Sync clientFormData from the matched client when resuming a tender (session expired/fresh)
  useEffect(() => {
    if (!clientData.id || clients.length === 0 || clientFormData.abn) return;
    const matchedClient = clients.find(c => c.id === clientData.id);
    if (!matchedClient) return;
    setClientFormData(prev => ({
      ...prev,
      abn: matchedClient.abn || prev.abn,
      channel_partner: matchedClient.channel_partner || prev.channel_partner,
      sf_account_id: matchedClient.sf_account_id || prev.sf_account_id,
      email: matchedClient.extn?.email || prev.email,
      phone: matchedClient.extn?.phone || prev.phone,
    }));
  }, [clientData.id, clients]);

  // Handle client selection from dropdown
  const handleClientSelect = (clientId: string) => {
    if (clientId === "" || clientId === "none") {
      // Clear selection
      setIsNewClient(false);
      setSelectedClientId("");
      setClientData({
        id: undefined,
        name: "",
        contactPerson: "",
        email: "",
        phone: "",
        company: "",
        industry: "",
      });
      setClientFormData({
        abn: "",
        acn: "",
        contact_name: "",
        channel_partner: "",
        sf_account_id: "",
        email: "",
        phone: "",
      });
      // Clear ABN validation
      setAbnValidation({ isValidating: false, isValid: null, message: "" });
    } else if (clientId === "new") {
      setIsNewClient(true);
      setSelectedClientId("new");
      setClientData({
        id: undefined,
        name: "",
        contactPerson: "",
        email: "",
        phone: "",
        company: "",
        industry: "",
      });
      setClientFormData({
        abn: "",
        acn: "",
        contact_name: "",
        channel_partner: "",
        sf_account_id: "",
        email: "",
        phone: "",
      });
      // Clear ABN validation when creating new client
      setAbnValidation({ isValidating: false, isValid: null, message: "" });
    } else {
      const selectedClient = clients.find(client => client.id === clientId);
      if (selectedClient) {
        setIsNewClient(false);
        setSelectedClientId(clientId);

        // Get email and phone from extn jsonb
        const email = selectedClient.extn?.email || "";
        const phone = selectedClient.extn?.phone || "";

        // Map database data to context structure
        setClientData({
          id: selectedClient.id,
          name: selectedClient.name,
          contactPerson: selectedClient.name,
          email: email,
          phone: phone,
          company: selectedClient.name,
          industry: selectedClient.extn?.industry || "Energy",
        });

        // Populate additional client fields
        setClientFormData({
          abn: selectedClient.abn || "",
          acn: (selectedClient as { acn?: string }).acn || "",
          contact_name: "",
          channel_partner: selectedClient.channel_partner || "",
          sf_account_id: selectedClient.sf_account_id || "",
          email: email,
          phone: phone,
        });

        // Trigger ABN validation for dropdown-selected client
        if (selectedClient.abn && selectedClient.abn.length === 11) {
          setTimeout(() => {
            validateABN(selectedClient.abn);
          }, 100);
        } else {
          // No ABN or invalid length - clear validation state (ABN is optional)
          setAbnValidation({ isValidating: false, isValid: null, message: "" });
        }
      }
    }
  };

  // Add new fields to client data structure
  const [clientFormData, setClientFormData] = useState({
    abn: "",
    // Some entities have an ACN and no ABN — see lib/companyIdentifier.ts.
    acn: "",
    contact_name: "",
    channel_partner: "",
    sf_account_id: "",
    email: "",
    phone: "",
  });

  // Ref to access latest clientFormData in callbacks
  const clientFormDataRef = useRef(clientFormData);

  // Update ref when clientFormData changes
  useEffect(() => {
    clientFormDataRef.current = clientFormData;
  }, [clientFormData]);

  // Handle form field changes - memoized to prevent input focus loss
  const handleFieldChange = useCallback((field: string, value: string) => {
    setClientData((prevClientData) => {
      const newClientData = {
        ...prevClientData,
        [field]: value
      };

      // Save to sessionStorage immediately for persistence (only if valid session)
      if (hasValidSession()) {
        const completeClientData = {
          ...newClientData,
          ...clientFormDataRef.current,
        };
        sessionStorage.setItem("scopingai_clientData", JSON.stringify(completeClientData));
      }

      return newClientData;
    });
  }, []);

  // ABN validation state
  const [abnValidation, setAbnValidation] = useState({
    isValidating: false,
    isValid: null,
    message: "",
  });

  /**
   * The consultant printed on the proposal's last page, captured here rather than guessed at
   * generation time. See saveTenderConsultant for why — in short, generation fell back to
   * whoever clicked Generate, and on 40% of prod tenders no source could supply a phone at all,
   * so {consultant_mobile} reached clients as a raw token.
   */
  const [consultant, setConsultant] = useState<TenderConsultant>({ name: "", email: "", phone: "" });
  /**
   * F9 — what the proposal cover is addressed to, when the broker states it.
   *
   * Empty means "use the client's name", which is every tender that has not been overridden.
   * A multi-site tender can cover several legal entities, and then no Client record is the
   * truthful answer — their words: "If multi-site has multiple entity names, we want to make
   * the 'Prepared for:' customizable."
   */
  const [preparedFor, setPreparedFor] = useState<string>("");
  const [consultantLoading, setConsultantLoading] = useState(true);
  const [consultantPrefillNote, setConsultantPrefillNote] = useState<string | null>(null);

  /**
   * Has the user typed in Broker Details themselves?
   *
   * A Salesforce import names the tender's owner, which beats the pre-fill from whoever is
   * signed in — but it must never overwrite something this person deliberately typed. A ref, not
   * state: it only has to be read at import time and must not re-render on every keystroke.
   */
  const consultantEditedRef = useRef(false);

  const consultantErrors = useMemo(() => {
    const errors: { name?: string; email?: string; phone?: string } = {};
    if (!consultant.name.trim()) errors.name = "Broker name is required";
    if (!consultant.email.trim()) errors.email = "Broker email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(consultant.email.trim())) errors.email = "Enter a valid email address";
    // Digits only, so "+61 400 000 000", "0400 000 000" and "(03) 9002 5131" all pass.
    if (!consultant.phone.trim()) errors.phone = "Broker phone is required";
    else if ((consultant.phone.match(/\d/g) || []).length < 8) errors.phone = "Enter a valid phone number";
    return errors;
  }, [consultant]);

  const isConsultantValid = Object.keys(consultantErrors).length === 0;

  /**
   * Pre-fill from the same two sources generation used, so the 58% of tenders that already
   * have complete details cost nobody any typing. Stored value wins — it is the one a human
   * has already confirmed on this tender.
   */
  useEffect(() => {
    let mounted = true;

    const loadConsultant = async () => {
      try {
        setConsultantLoading(true);

        if (tenderId) {
          getTenderPreparedFor(tenderId).then(stored => { if (mounted) setPreparedFor(stored); });
          const stored = await getTenderConsultant(tenderId);
          if (!mounted) return;
          if (stored && (stored.name || stored.email || stored.phone)) {
            setConsultant(stored);
            setConsultantPrefillNote(null);
            return;
          }
        }

        const { data: auth } = await supabase.auth.getUser();
        const userId = auth?.user?.id;
        if (!userId) return;

        const { data: profile } = await supabase
          .from("base_profiles")
          .select("name, email, phone")
          .eq("id", userId)
          .single();
        if (!mounted) return;

        const prefilled = {
          name: profile?.name?.trim() || "",
          email: profile?.email?.trim() || "",
          phone: profile?.phone?.trim() || "",
        };

        /**
         * ⚠️ NEVER OVERWRITE A FORM THAT IS ALREADY FILLED — THIS EFFECT RE-RUNS MID-SAVE.
         *
         * It is keyed on [tenderId], and tenderId changes from null to a real id INSIDE
         * handleNext, when createTenderWorkflow runs. handleNext then keeps going for another
         * ~50 lines before it reaches saveTenderConsultant:
         *
         *     createTenderWorkflow(...)        -> setTenderId(new)  -- this effect re-fires
         *     storeImportRecord(...)                                -- loadConsultant() runs
         *     save client / sites / step data                       -- getTenderConsultant()
         *     saveTenderConsultant(id, consultant)                  -- ...finds NOTHING yet
         *
         * So the stored-consultant lookup above comes back empty, execution falls through to
         * here, and the signed-in user's own profile replaced the Salesforce tender owner that
         * the import had just seeded. The form then showed the operator's name and email with an
         * empty phone, which is the reported bug — and it bit MULTI-SITE tenders hardest,
         * because more sites mean more awaits between tender creation and the consultant save,
         * widening the window this effect lands in.
         *
         * A functional update is required, not a check against `consultant`: this callback
         * closed over the render in which it was created, so the captured value is stale by the
         * time the awaits above resolve. `prev` is the live one.
         */
        let applied = false;
        setConsultant(prev => {
          const alreadyFilled = Boolean(prev.name?.trim() || prev.email?.trim() || prev.phone?.trim());
          if (alreadyFilled) return prev;   // Salesforce seed, or the user's own typing — keep it
          applied = true;
          return prefilled;
        });

        // Every base_profiles row measured on prod has an email and no phone, so say plainly
        // that the missing piece has to be typed rather than leaving a silent empty box.
        // Only speak for a prefill that actually happened — otherwise this note would contradict
        // the Salesforce note already on screen.
        if (applied) {
          setConsultantPrefillNote(
            prefilled.phone ? null : "Pre-filled from your profile. Add a contact number — it prints on the proposal.",
          );
        }
      } catch (error: unknown) {
        console.error("Failed to pre-fill consultant details:", error);
      } finally {
        if (mounted) setConsultantLoading(false);
      }
    };

    loadConsultant();
    return () => { mounted = false; };
  }, [tenderId]);

  // Force re-render when validation state changes to update button
  useEffect(() => {
    // This effect will trigger whenever validation-related state changes
    const isValid = isMandatoryFieldsFilled();
    console.log('🔄 Validation state changed:', isValid);
  }, [clientData, clientFormData, abnValidation, sites]);

  // ABN checksum validation (Australian algorithm)
  const validateABNChecksum = (abn: string): boolean => {
    if (abn.length !== 11) return false;

    const weights = [10, 1, 3, 5, 7, 9, 11, 13, 15, 17, 19];
    let sum = 0;

    // Subtract 1 from the first digit
    const firstDigit = parseInt(abn[0]) - 1;
    sum += firstDigit * weights[0];

    // Add weighted sum of remaining digits
    for (let i = 1; i < 11; i++) {
      sum += parseInt(abn[i]) * weights[i];
    }

    return sum % 89 === 0;
  };

  // ABN validation function using our backend API
  const validateABN = async (abn: string) => {
    if (!abn || abn.length !== 11) {
      setAbnValidation({ isValidating: false, isValid: false, message: "ABN must be 11 digits" });
      return;
    }

    // First check ABN checksum locally for immediate feedback
    if (!validateABNChecksum(abn)) {
      setAbnValidation({ isValidating: false, isValid: false, message: "Invalid ABN format" });
      return;
    }

    setAbnValidation({ isValidating: true, isValid: true, message: "Validating ABN..." });

    try {
      // Call our backend API endpoint
      const response = await fetch('/api/scopingai/validate-abn', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ abn }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const validationData = result.data;

        setAbnValidation({
          isValidating: false,
          isValid: validationData.isValid,
          message: validationData.message,
        });

        // Store additional business information if available
        if (validationData.isValid && validationData.entityName) {
          console.log('✅ ABN Validation Success:', {
            abn: validationData.abn,
            entityName: validationData.entityName,
            entityType: validationData.entityType,
            gstStatus: validationData.gstStatus,
            source: validationData.source
          });
        }
      } else {
        throw new Error(result.error || 'Validation failed');
      }

    } catch (error) {
      console.error('❌ ABN validation error:', error);

      // Fallback to checksum validation
      const checksumValid = validateABNChecksum(abn);
      setAbnValidation({
        isValidating: false,
        isValid: checksumValid,
        message: checksumValid
          ? "ABN format is valid (validation service unavailable)"
          : "Invalid ABN format"
      });
    }
  };

  // Re-validate ABN when navigating back to step 1
  useEffect(() => {
    // Only re-validate if we have an ABN and validation state is null (not yet validated)
    if (clientFormData.abn && clientFormData.abn.length === 11 && abnValidation.isValid === null) {
      console.log("🔄 Re-validating ABN after navigation:", clientFormData.abn);
      validateABN(clientFormData.abn);
    }
  }, [clientFormData.abn]); // Run when ABN changes or component mounts

  // Update client form data with ABN validation - memoized to prevent input focus loss
  const updateClientFormData = useCallback((field: string, value: string) => {
    setClientFormData((prevFormData) => {
      const newFormData = {
        ...prevFormData,
        [field]: value
      };

      // Also update context clientData for email and phone fields
      if (field === 'email' || field === 'phone') {
        setClientData((prevClientData) => ({
          ...prevClientData,
          [field]: value
        }));
      }

      // Save to sessionStorage immediately for persistence (only if valid session)
      if (hasValidSession()) {
        setClientData((prevClientData) => {
          const completeClientData = {
            ...prevClientData,
            ...newFormData,
          };
          sessionStorage.setItem("scopingai_clientData", JSON.stringify(completeClientData));
          return prevClientData;
        });
      }

      return newFormData;
    });

    // Trigger ABN validation when ABN field changes (outside state updater)
    if (field === 'abn') {
      if (value.length === 11) {
        validateABN(value);
      } else if (value.length > 0 && value.length < 11) {
        // Show immediate feedback for incomplete ABN
        setAbnValidation({
          isValidating: false,
          isValid: false,
          message: `ABN must be 11 digits (${value.length}/11)`
        });
      } else {
        // Clear validation when empty
        setAbnValidation({ isValidating: false, isValid: null, message: "" });
      }
    }
  }, []);

  // Check if mandatory fields are filled - ENABLED
  const isMandatoryFieldsFilled = () => {
    // Mandatory fields:
    // 1. Tender/Proposal Name
    // 2. Client Name
    // 3. Email (valid format)
    // 4. ABN (11 digits AND validated)
    // 5. At least one site
    // 6. All sites must be valid (name, NMI, address)

    const hasTenderName = tenderName?.trim().length > 0;
    const hasClientName = clientData.name?.trim().length > 0;
    const hasEmail = clientFormData.email?.trim().length > 0;
    const hasValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientFormData.email || '');
    /**
     * ⚠️ AN ABN *OR* AN ACN — NOT AN ABN ALWAYS.
     *
     * Requiring an ABN meant an ACN-only company could not pass Step 1, so people typed a
     * plausible ABN to get through. That invented number is written to client.abn and printed on
     * the proposal, which is the bug this removes. An ABN supplied is still checksum-validated.
     */
    const hasIdentifier = hasCompanyIdentifier(
      clientFormData.abn,
      clientFormData.acn,
      abnValidation.isValid === true,
    );

    // Check sites validation
    const hasSites = sites.length > 0;
    const allSitesValid = sites.every(site => validateSite(site).isValid);

    const isValid = hasTenderName && hasClientName && hasEmail && hasValidEmail && hasIdentifier && hasSites && allSitesValid
      // 7. Consultant name, email and phone — they print on the proposal's last page and
      //    nothing downstream can invent them. See saveTenderConsultant.
      && isConsultantValid;

    return isValid;
  };

  // Handle Salesforce import
  const handleImportFromSalesforce = async (skipExistingCheck = false) => {
    const oppId = sfOpportunityIdInput.trim();
    const tenId = sfTenderIdInput.trim();

    if (!oppId && !tenId) {
      toast({ title: "Validation Error", description: "Please enter a Salesforce Opportunity ID or Tender ID.", variant: "destructive" });
      return;
    }

    const inputId = tenId || oppId;
    const idType: 'opportunity' | 'tender' = tenId ? 'tender' : 'opportunity';

    // Guard: already imported for this tender — show re-import dialog
    if (sfImportExists && !skipExistingCheck) {
      setShowReimportDialog(true);
      return;
    }

    setIsImporting(true);
    setShowReimportDialog(false);

    try {
      const result = await salesforceImportService.importFromOpportunity(inputId, idType);

      if (!result.success || !result.data) {
        toast({ title: "Import Failed", description: result.error || "Could not import from Salesforce.", variant: "destructive" });
        return;
      }

      const normalized = salesforceImportService.normalizePayload(result.data);

      // Clear any stale sessionStorage client data so it doesn't restore over SF data
      sessionStorage.removeItem('scopingai_clientData');

      // Check match — query DB directly to avoid stale in-memory list
      // Priority: sf_account_id → contact email (covers cross-device imports)
      let existingClient: typeof clients[0] | null = null;
      const orgId = await getUserOrganizationId();

      // sf_account_id stored = Account ID if present, else Opportunity ID as fallback
      const sfMatchId = normalized.sfAccountId || normalized.sfOpportunityId || null;

      if (orgId) {
        if (sfMatchId) {
          const { data: byAccount } = await supabase
            .from('client')
            .select('*')
            .eq('org_id', orgId)
            .eq('sf_account_id', sfMatchId)
            .limit(1)
            .single();
          if (byAccount) existingClient = byAccount;
        }

        if (!existingClient && normalized.contactEmail) {
          const { data: byEmail } = await supabase
            .from('client')
            .select('*')
            .eq('org_id', orgId)
            .filter('extn->>email', 'eq', normalized.contactEmail)
            .limit(1)
            .single();
          if (byEmail) existingClient = byEmail;
        }
      }

      // Build client data purely from SF — contact name as client name, contact email as email
      const sfClientName = normalized.contactName || normalized.clientName || normalized.opportunityName || '';
      const freshClientData = {
        id: existingClient?.id,
        name: sfClientName,
        contactPerson: normalized.contactName || '',
        email: normalized.contactEmail || '',
        phone: normalized.accountPhone || '',
        company: normalized.clientName || '',
        industry: '',
      };
      const freshFormData = {
        abn: normalized.abn || '',
        // Salesforce already carries the ACN — QBG GROUP came in with one and no ABN.
        acn: normalized.acn || '',
        contact_name: normalized.contactName || '',
        channel_partner: '',
        sf_account_id: normalized.sfAccountId || normalized.sfOpportunityId || '',
        email: normalized.contactEmail || '',
        phone: normalized.accountPhone || '',
      };

      // Apply to form — completely replacing any previous selection
      setClientData(freshClientData);
      setClientFormData(freshFormData);
      setIsNewClient(!existingClient);
      setSelectedClientId(existingClient ? existingClient.id : 'new');

      if (existingClient) {
        toast({ title: 'Client matched', description: `Linked to existing client in CRM. Fields updated from Salesforce.` });
      } else {
        toast({ title: 'New client from Salesforce', description: 'Client not found in CRM. Click "Save Client" or "Continue" to create it.' });
      }

      // Trigger ABN validation if ABN was populated
      if (normalized.abn && normalized.abn.length === 11) {
        setTimeout(() => validateABN(normalized.abn as string), 100);
      }

      if (normalized.entityName) {
        setTenderName(normalized.entityName);
      }

      // Store SF IDs in context for TenderDetailsStep
      setSfOpportunityId(normalized.sfOpportunityId);
      if (normalized.sfTenderId) setSfTenderId(normalized.sfTenderId);

      // Helper: Date → YYYY-MM-DD string
      const dateToStr = (d: Date | null) => d ? d.toISOString().split('T')[0] : '';

      // --- Populate tenderDetails (Step 2) with all Tender__c fields ---
      setTenderDetails((prev: Record<string, unknown>) => ({
        ...prev,
        sf_opportunity_id: normalized.sfOpportunityId,
        ...(normalized.entityName ? { entity_name: normalized.entityName } : {}),
        ...(normalized.abn ? { abn: normalized.abn } : {}),
        ...(normalized.acn ? { acn: normalized.acn } : {}),
        ...(normalized.currentContractExpiry ? { current_contract_expiry: dateToStr(normalized.currentContractExpiry) } : {}),
        ...(normalized.tenderDueDate ? { due_at: dateToStr(normalized.tenderDueDate) } : {}),
        ...(normalized.currentMeterProvider ? { current_meter_provider: normalized.currentMeterProvider } : {}),
        ...(normalized.currentRetailer ? { current_retailer: normalized.currentRetailer } : {}),
        ...(normalized.dmaProvider ? { dma_provider: normalized.dmaProvider } : {}),
        ...(normalized.retailerComms ? { retailer_comms: normalized.retailerComms } : {}),
        ...(normalized.createDma !== null ? { create_dma: normalized.createDma } : {}),
        ...(normalized.requestMeterData !== null ? { request_meter_data: normalized.requestMeterData } : {}),
        ...(normalized.requestBills !== null ? { request_bills: normalized.requestBills } : {}),
        ...(normalized.acquireCed !== null ? { acquire_ced: normalized.acquireCed } : {}),
        ...(normalized.retailerNotes ? { notes: normalized.retailerNotes } : {}),
        ...(normalized.address.street ? { addr_street: normalized.address.street } : {}),
        ...(normalized.address.city ? { addr_city: normalized.address.city } : {}),
        ...(normalized.address.state ? { addr_state: normalized.address.state } : {}),
        ...(normalized.address.postalCode ? { addr_postcode: normalized.address.postalCode } : {}),
        ...(normalized.address.country ? { addr_country: normalized.address.country } : {}),
      }));

      // --- Apply SF fields to sites ---
      // Salesforce models 1 Opportunity (+ its linked Tender__c) = 1 site. This
      // top-level import is for the FIRST site (and for tender-wide shared
      // fields, applied to every site below). For a multi-site tender, import
      // each additional site's own Opportunity from that site's own "Import
      // from Salesforce" button on its SiteCard (SiteCard.tsx) — that targets
      // only that site, so multiple Opportunities can be brought in side by side.
      // Site-specific fields (NMI, address, kWh usage, kVA) → first site only
      // Shared tender fields (dates, retailer info, config flags, ABN/ACN) → all sites
      if (sites.length > 0) {
        setSites(sites.map((site, index) => {
          const isFirst = index === 0;
          return {
            ...site,
            // Shared fields — apply to all sites
            ...(normalized.abn ? { abn: normalized.abn } : {}),
            ...(normalized.acn ? { acn: normalized.acn } : {}),
            ...(normalized.currentContractExpiry !== null ? { currentContractExpiry: normalized.currentContractExpiry as Date } : {}),
            ...(normalized.tenderDueDate !== null ? { dueAt: normalized.tenderDueDate as Date } : {}),
            ...(normalized.currentMeterProvider ? { currentMeterProvider: normalized.currentMeterProvider } : {}),
            ...(normalized.currentRetailer ? { currentRetailer: normalized.currentRetailer } : {}),
            ...(normalized.dmaProvider ? { dmaProvider: normalized.dmaProvider } : {}),
            ...(normalized.retailerComms ? { retailerComms: normalized.retailerComms } : {}),
            ...(normalized.createDma !== null ? { createDma: normalized.createDma } : {}),
            ...(normalized.requestMeterData !== null ? { requestMeterData: normalized.requestMeterData } : {}),
            ...(normalized.requestBills !== null ? { requestBills: normalized.requestBills } : {}),
            ...(normalized.acquireCed !== null ? { acquireCed: normalized.acquireCed } : {}),
            ...(normalized.retailerNotes ? { retailerNotes: normalized.retailerNotes } : {}),
            // Site-specific fields — first site only
            ...(isFirst ? { name: normalized.entityName || normalized.tenderName || site.name } : {}),
            ...(isFirst && normalized.nmi ? { nmi: normalized.nmi } : {}),
            ...(isFirst && normalized.annualKwhPeak !== null ? { annualKwhPeak: normalized.annualKwhPeak } : {}),
            ...(isFirst && normalized.annualKwhOffpeak !== null ? { annualKwhOffpeak: normalized.annualKwhOffpeak } : {}),
            ...(isFirst && normalized.annualKwhShoulder !== null ? { annualKwhShoulder: normalized.annualKwhShoulder } : {}),
            ...(isFirst && normalized.annualKwhTotal !== null ? { annualKwhTotal: normalized.annualKwhTotal } : {}),
            address: {
              ...site.address,
              ...(isFirst && normalized.address.street ? { street: normalized.address.street } : {}),
              ...(isFirst && normalized.address.city ? { city: normalized.address.city } : {}),
              ...(isFirst && normalized.address.state ? { state: normalized.address.state } : {}),
              ...(isFirst && normalized.address.postalCode ? { postcode: normalized.address.postalCode } : {}),
              ...(isFirst && normalized.address.country ? { country: normalized.address.country } : {}),
            },
          };
        }));
      }

      /**
       * --- Seed Broker Details from the imported tender owner ---
       *
       * ⚠️ WITHOUT THIS, THE CLIENT'S PHONE NUMBER ENDS UP ON THE PROPOSAL AS THE BROKER'S.
       *
       * The import filled every client field and wrote the owner to tender.extn.consultant, but
       * left Broker Details as the signed-in user's profile pre-fill — which carries no phone on
       * every prod profile measured. Broker Phone is mandatory, so the user had to type into an
       * empty box with the client's number sitting right above it, and handleNext then saved
       * that form value over the real owner (saveTenderConsultant runs AFTER the import flush).
       *
       * On prod, 12 of the 25 most recent tenders with both an import record and a stored
       * consultant had extn.consultant.phone character-for-character equal to the imported
       * Account.Phone. Seeding the form closes that gap at the source.
       *
       * mergeSalesforceConsultant never overwrites what the user typed themselves.
       */
      if (normalized.consultant) {
        const merged = mergeSalesforceConsultant(consultant, normalized.consultant, consultantEditedRef.current);
        setConsultant(merged);
        // Salesforce owner records often carry neither Phone nor MobilePhone. Say so, rather
        // than leaving a mandatory empty box next to the client's number.
        setConsultantPrefillNote(
          merged.phone
            ? null
            : "Broker details filled from the Salesforce tender owner. Add their contact number — it prints on the proposal, so do not use the client's.",
        );
      }

      // --- Store import record in DB (linked to tender) ---
      if (tenderId) {
        // Tender already exists — store immediately
        const storeResult = await salesforceImportService.storeImportRecord(
          tenderId,
          normalized.sfOpportunityId,
          normalized.sfTenderId,
          result.data,
          normalized.fieldMappingLog
        );
        if (!storeResult.success) {
          console.warn('⚠️ SF import applied to form but could not be saved to database:', storeResult.error);
        }

        // Update tender's opportunity_name and extn.consultant so they persist on reload
        const tenderUpdate: Record<string, unknown> = {};
        if (normalized.entityName) tenderUpdate.opportunity_name = normalized.entityName;
        if (normalized.consultant) tenderUpdate.extn = { consultant: normalized.consultant };
        if (Object.keys(tenderUpdate).length > 0) {
          await supabase
            .from('tender')
            .update(tenderUpdate)
            .eq('id', tenderId);
        }

        setSfImportExists(true);
        setSfImportedAt(new Date().toISOString());
      } else {
        // New tender — tender doesn't exist yet, save pending to store after tender is created
        setPendingSfImport({
          sfOpportunityId: normalized.sfOpportunityId,
          sfTenderId: normalized.sfTenderId ?? null,
          rawPayload: result.data,
          fieldMappingLog: normalized.fieldMappingLog,
          consultant: normalized.consultant,
        });
      }

      // --- Success toast ---
      const source = result.data.tender ? 'Opportunity + Tender data applied.' : 'Opportunity data only — no linked Tender found.';
      const siteNote = sites.length > 0 ? ` Values applied to ${sites.length} site(s).` : '';
      toast({ title: "Imported from Salesforce", description: `${source}${siteNote}` });

    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast({ title: "Import Error", description: message, variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  // Mock save function
  const handleSave = () => {
    const completeClientData = {
      ...clientData,
      ...clientFormData,
    };
    console.log("Saving complete client data:", completeClientData);
    // Store in session storage for context compatibility
    sessionStorage.setItem("scopingai_clientData", JSON.stringify(completeClientData));
  };

  // Save client function - works like the clients page
  const handleSaveClient = async () => {
    if (!clientData.name?.trim()) {
      toast({
        title: "Validation Error",
        description: "Client name is required",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const result = await createClient({
        name: clientData.name,
        contact_name: clientFormData.contact_name,
        abn: clientFormData.abn,
        acn: clientFormData.acn,
        channel_partner: clientFormData.channel_partner,
        sf_account_id: clientFormData.sf_account_id,
        email: clientFormData.email,
        phone: clientFormData.phone,
        industry: clientData.industry || "Energy",
      });

      if (result.success && result.data) {
        toast({
          title: "Success",
          description: "Client created successfully",
        });

        // Update the client data with the created client
        setClientData({
          ...clientData,
          id: result.data.id,
        });

        // Switch to existing client mode
        setSelectedClientId(result.data.id);
        setIsNewClient(false);

        // Refresh the clients list
        await loadClients();
      } else {
        toast({
          title: "Error",
          description: result.error || "Failed to create client",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error creating client:", error);
      toast({
        title: "Error",
        description: "Failed to create client",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Create new client in database
  const createNewClient = async (): Promise<string | null> => {
    if (!orgId) {
      toast({
        title: "Error",
        description: "Organization ID not found",
        variant: "destructive",
      });
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("client")
        .insert({
          org_id: orgId,
          name: clientData.name,
          abn: clientFormData.abn || null,
          acn: clientFormData.acn || null,
          channel_partner: clientFormData.channel_partner || null,
          sf_account_id: clientFormData.sf_account_id || null,
          extn: {
            email: clientFormData.email,
            phone: clientFormData.phone,
            industry: clientData.industry || "Energy",
          },
        })
        .select("id")
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: `Client "${clientData.name}" created successfully`,
      });

      // Refresh clients list
      const { data: updatedClients } = await supabase
        .from("client")
        .select("*")
        .eq("org_id", orgId)
        .order("created_at", { ascending: false });

      if (updatedClients) {
        setClients(updatedClients);
      }

      return data.id;
    } catch (error: any) {
      console.error("Error creating client:", error);
      toast({
        title: "Error",
        description: error.message || "Could not create client",
        variant: "destructive",
      });
      return null;
    }
  };

  // Helper to save sites to global table and link to tender
  const saveSitesToGlobalAndLink = async (
    sitesToSave: any[],
    clientId: string,
    tenderId: string,
    orgId: string
  ): Promise<any[]> => {
    const updatedSites = [...sitesToSave];
    const { updateSite } = await import('../services/siteService');

    // 1. Save to Global Site Table
    for (let i = 0; i < updatedSites.length; i++) {
      const site = updatedSites[i];

      // Determine if we have a global site ID (siteId)
      // If site.siteId exists, use it.
      // If site.id is a UUID (not temp), and we don't have siteId, it might be ambiguous, 
      // but in our new logic, site.id should be tender_site.id (if linked) or temp ID.
      // So we rely on site.siteId.

      let globalSiteId = site.siteId;

      if (globalSiteId) {
        // Update existing global site
        const updateResult = await updateSite(globalSiteId, {
          ...site,
          id: globalSiteId, // updateSite expects ID in the object
          documentFilePaths: site.documentFilePaths || [],
          retailerNotes: site.retailerNotes || null,
        });

        if (updateResult.success) {
          console.log(`✅ Updated global site: ${site.name} (${globalSiteId})`);
        } else {
          console.error(`❌ Failed to update global site "${site.name}":`, updateResult.error);
        }
      } else {
        /**
         * ⚠️ MATCH ON THE 10-CHARACTER BASE, NOT THE RAW STRING.
         *
         * Bill Validation stores the FULL NMI including the checksum digit — 5,383 of prod's
         * 5,411 sites are eleven characters — and its own lookups use nmiBase for exactly this
         * reason. A tender typically carries the ten-character identifier, so `.eq('nmi', ...)`
         * never matched a BV site and created a second row for the same meter. Measured on
         * prod: 421 NMIs already have duplicate site rows, most across two or three different
         * client records.
         *
         * Three things were wrong and all three are fixed here:
         *   1. exact match -> compare the 10-character base, the way BV does
         *   2. scoped to client_id -> an NMI identifies a physical connection point, not a
         *      customer record. The same meter under another client is still the same site,
         *      and that is where the duplicates came from.
         *   3. .single() -> errored whenever two rows already matched, returning nothing and
         *      creating a THIRD. maybeSingle + ordering takes the earliest existing row instead.
         *
         * ⚠️ SCOPED TO THE ORGANISATION, THOUGH — NEVER GLOBALLY.
         *
         * Dropping client_id is right; dropping org_id would not be. site is a tenant table and
         * two organisations can legitimately hold the same NMI as separate records, so matching
         * across the whole table would let one org's tender adopt another org's site row. RLS
         * would very likely refuse the read, but a privacy boundary must be stated in the query
         * rather than left to a policy to catch.
         */
        const base = nmiBase(site.nmi);
        const { data: existingSite } = base
          ? await supabase
              .from("site")
              .select("id, nmi, client_id, created_at")
              .eq("org_id", orgId)
              .ilike("nmi", `${base}%`)
              .order("created_at", { ascending: true })
              .limit(1)
              .maybeSingle()
          : { data: null };

        if (existingSite) {
          if (existingSite.client_id !== clientId) {
            // Reused, but NOT re-parented: the row may carry Bill Validation contracts and
            // readings that belong to the client it is filed under. Surfacing it is the most
            // this should do on its own.
            console.warn(
              `⚠️ NMI ${site.nmi} already exists under a different client (site ${existingSite.id}) — reusing that site rather than creating a duplicate. Its client_id is left as it is.`,
            );
          }
          globalSiteId = existingSite.id;
          // Update it
          await updateSite(globalSiteId, {
            ...site,
            id: globalSiteId,
            documentFilePaths: site.documentFilePaths || [],
            retailerNotes: site.retailerNotes || null,
          });
          console.log(`✅ Found and updated existing global site: ${site.name} (${globalSiteId})`);
        } else {
          // Create new global site
          const siteResult = await createSite({
            org_id: orgId,
            client_id: clientId,
            site: site,
          });

          if (siteResult.success && siteResult.data) {
            globalSiteId = siteResult.data.id;
            console.log(`✅ Created new global site: ${site.name} (${globalSiteId})`);
          } else {
            console.error(`❌ Failed to create global site "${site.name}":`, siteResult.error);
            throw new Error(`Failed to save site "${site.name}"`);
          }
        }
      }

      // Update the site object with the global ID
      updatedSites[i] = { ...site, siteId: globalSiteId };
    }

    // 2. Link to Tender (syncTenderSites)
    const tenderSitesData = updatedSites.map((site) => ({
      id: site.id && !site.id.startsWith('site_') ? site.id : undefined, // This is the tender_site ID (if it exists)
      site_id: site.siteId!, // This is the Global Site ID (Must exist now)
      name: site.name,
      nmi: site.nmi,
      address: site.address,
      annualUsage: {
        peak: site.annualKwhPeak,
        offPeak: site.annualKwhOffpeak,
        shoulder: site.annualKwhShoulder,
      },
      abn: site.abn,
      acn: site.acn,
      due_at: (site.dueAt && typeof site.dueAt.toISOString === 'function') ? site.dueAt.toISOString() : site.dueAt,
      current_contract_expiry: (site.currentContractExpiry && typeof site.currentContractExpiry.toISOString === 'function') ? site.currentContractExpiry.toISOString() : site.currentContractExpiry,
      current_meter_provider: site.currentMeterProvider,
      current_retailer: site.currentRetailer,
      dma_provider: site.dmaProvider,
      retailer_comms: site.retailerComms,
      create_dma: site.createDma,
      request_meter_data: site.requestMeterData,
      request_bills: site.requestBills,
      acquire_ced: site.acquireCed,
      peak_demand_kw: site.peakDemandKw,
      current_contract_rates: site.currentContractRates as Record<string, unknown> | undefined,
      daily_charges: site.dailyCharges,
      non_energy_charges: site.nonEnergyCharges,
    }));

    const syncResult = await syncTenderSites({
      tender_id: tenderId,
      org_id: orgId,
      tender_defaults: {},
      sites: tenderSitesData,
    });

    if (!syncResult.success || !syncResult.data) {
      throw new Error(syncResult.error || "Failed to link sites to tender");
    }

    // 3. Update sites with the returned tender_site IDs
    // We need to map back the results to our sites array
    // syncResult.data contains the tender_site records
    const finalSites = updatedSites.map(site => {
      // Find the corresponding result. 
      // We can match by site_id (Global ID) which should be unique per tender? 
      // Yes, a site shouldn't be added twice to the same tender usually.
      const linkedSite = syncResult.data?.find(ts => ts.site_id === site.siteId);
      if (linkedSite) {
        return { ...site, id: linkedSite.id }; // Update id to be the tender_site ID
      }
      return site;
    });

    return finalSites;
  };

  const handleNext = async () => {
    // Always show site errors from the first Continue click
    setShowSiteErrors(true);

    // Validate client-level mandatory fields and show specific toast messages
    const missingClientFields: string[] = [];
    if (!tenderName?.trim()) missingClientFields.push('Proposal Name');
    if (!clientData.name?.trim()) missingClientFields.push('Client Name');
    if (!clientFormData.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(clientFormData.email || '')) missingClientFields.push('Valid Email');
    if (!hasCompanyIdentifier(clientFormData.abn, clientFormData.acn, abnValidation.isValid === true)) {
      missingClientFields.push(companyIdentifierError);
    }
    // Named individually rather than as one "Broker Details" so the toast says which box to fill.
    if (consultantErrors.name) missingClientFields.push('Broker Name');
    if (consultantErrors.email) missingClientFields.push('Broker Email');
    if (consultantErrors.phone) missingClientFields.push('Broker Phone');

    if (missingClientFields.length > 0) {
      toast({
        title: 'Required Fields Missing',
        description: `Please fill in: ${missingClientFields.join(', ')}`,
        variant: 'destructive',
      });
      return;
    }

    // Validate sites
    if (sites.length === 0) {
      toast({
        title: 'Sites Required',
        description: 'Please add at least one site before continuing.',
        variant: 'destructive',
      });
      return;
    }

    const siteIssues: string[] = [];
    sites.forEach((site, idx) => {
      const v = validateSite(site);
      if (!v.isValid) {
        const siteName = site.name || `Site ${idx + 1}`;
        const fields: string[] = [];
        if (v.errors.name) fields.push('Site Name');
        if (v.errors.nmi) fields.push('NMI');
        if (v.errors.address?.street) fields.push('Street Address');
        if (v.errors.currentContractExpiry) fields.push('Current Contract Expiry');
        if (v.errors.annualKwhPeak) fields.push('Peak Usage (kWh)');
        if (v.errors.annualKwhOffpeak) fields.push('Off-Peak Usage (kWh)');
        if (v.errors.energyRates?.peak_rate) fields.push('Peak Rate');
        if (v.errors.energyRates?.offpeak_rate) fields.push('Off-Peak Rate');
        if (v.errors.environmental?.veec_rate) fields.push('VEEC Rate');
        if (v.errors.environmental?.lrec_rate) fields.push('LREC Rate');
        if (v.errors.environmental?.srec_rate) fields.push('SREC Rate');
        if (fields.length > 0) siteIssues.push(`${siteName}: ${fields.join(', ')}`);
      }
    });

    if (siteIssues.length > 0) {
      setShowSiteErrors(true);
      toast({
        title: 'Site Information Incomplete',
        description: siteIssues.join(' | '),
        variant: 'destructive',
      });
      return;
    }

    setIsSaving(true);

    try {
      // Save client data to session
      handleSave();

      // STEP 0: Check if tender name is unique (only for new tenders)
      if (tenderName && orgId && !tenderId) {
        const { data: existingTender, error: tenderCheckError } = await supabase
          .from("tender")
          .select("id")
          .eq("org_id", orgId)
          .eq("opportunity_name", tenderName)
          .limit(1)
          .single();

        if (existingTender && !tenderCheckError) {
          toast({
            title: "Duplicate Proposal Name",
            description: `A proposal with the name "${tenderName}" already exists. Please use a unique name.`,
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }
      }

      // STEP 1: Save/Update Client
      let clientId = clientData.id || selectedClientId;

      if (isNewClient && clientData.name) {
        // Create new client
        const clientResult = await createClient({
          name: clientData.name,
          contact_name: clientFormData.contact_name,
          abn: clientFormData.abn,
          channel_partner: clientFormData.channel_partner,
          sf_account_id: clientFormData.sf_account_id,
          email: clientFormData.email,
          phone: clientFormData.phone,
          industry: clientData.industry || "Energy",
        });

        if (!clientResult.success || !clientResult.data) {
          toast({
            title: "Error",
            description: clientResult.error || "Failed to create client",
            variant: "destructive",
          });
          setIsSaving(false);
          return;
        }

        clientId = clientResult.data.id;

        // Update context with new client ID and selected client ID
        setClientData({
          ...clientData,
          id: clientId,
        });
        setSelectedClientId(clientId);

        console.log("✅ Created new client:", clientId);
      } else if (clientId && clientId !== "new" && clientId !== "none") {
        // Update existing client with any modified field values
        await updateClient(clientId, {
          name: clientData.name,
          contact_name: clientFormData.contact_name,
          abn: clientFormData.abn,
          channel_partner: clientFormData.channel_partner,
          sf_account_id: clientFormData.sf_account_id,
          email: clientFormData.email,
          phone: clientFormData.phone,
          industry: clientData.industry || "Energy",
        });
        console.log("✅ Updated existing client:", clientId);
      }

      // STEP 2: Create Tender Workflow if needed
      let currentTenderId = tenderId;
      if (!currentTenderId) {
        const tenderResult = await createTenderWorkflow(clientId, tenderName);
        if (!tenderResult.success) {
          throw new Error(tenderResult.error || 'Failed to create tender');
        }
        currentTenderId = tenderResult.tenderId;

        // Flush pending SF import now that tender exists
        if (pendingSfImport && currentTenderId) {
          const storeResult = await salesforceImportService.storeImportRecord(
            currentTenderId,
            pendingSfImport.sfOpportunityId,
            pendingSfImport.sfTenderId,
            pendingSfImport.rawPayload,
            pendingSfImport.fieldMappingLog
          );
          if (!storeResult.success) {
            console.warn('⚠️ Could not save pending SF import record:', storeResult.error);
          }
          if (pendingSfImport.consultant) {
            await supabase
              .from('tender')
              .update({ extn: { consultant: pendingSfImport.consultant } })
              .eq('id', currentTenderId);
          } else {
            console.warn('⚠️ SF import flushed but consultant data is null — extn.consultant not written');
          }
          setSfImportExists(true);
          setSfImportedAt(new Date().toISOString());
          setPendingSfImport(null);
        }
      } else {
        // Ensure tender is updated with latest client/name
        await updateTender(currentTenderId, {
          opportunity_name: tenderName,
          client_id: clientId,
        });
      }

      // STEP 3: Save Sites (Global + Link)
      if (sites.length > 0 && clientId && currentTenderId && orgId) {
        const updatedSites = await saveSitesToGlobalAndLink(sites, clientId, currentTenderId, orgId);

        // Update context with fully saved sites
        setSites(updatedSites);
        console.log("✅ Saved sites and linked to tender:", updatedSites);

        // Save step data to workflow (just in case, though we rely on relational tables now)
        await saveCurrentStepData(1, {
          clientData,
          sites: updatedSites,
          tenderName,
          selectedClientId: clientId,
        }, true);
      }

      // Pin the consultant to the TENDER. Generation reads tender.extn.consultant first, so
      // from here the proposal carries these details no matter who exports it later.
      if (currentTenderId) {
        // Saved alongside the consultant because both live on tender.extn and both are read at
        // generation time. An empty value clears the override rather than pinning a blank name.
        const preparedForResult = await saveTenderPreparedFor(currentTenderId, preparedFor);
        if (!preparedForResult.success) {
          toast({
            title: '"Prepared for" not saved',
            description: `${preparedForResult.error || "Unknown error"}. The cover will use the client's name.`,
            variant: "destructive",
          });
        }
        const consultantResult = await saveTenderConsultant(currentTenderId, consultant);
        if (!consultantResult.success) {
          // Not fatal — the client and sites are already saved and the user can continue — but
          // it must not pass silently, or the proposal quietly reverts to guessing.
          toast({
            title: "Broker details not saved",
            description: `${consultantResult.error || "Unknown error"}. Re-enter them on this step before generating the proposal.`,
            variant: "destructive",
          });
        }
      }

      toast({
        title: "Success",
        description: `Client and sites saved successfully`,
      });

      // Proceed to next step
      contextHandleNext();
    } catch (error: any) {
      console.error("Error in handleNext:", error);
      toast({
        title: "Error",
        description: error.message || "An error occurred while saving data",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!tenderName?.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a proposal name before saving.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      // 1. Handle Client
      let clientId = clientData.id || selectedClientId;

      if (isNewClient && clientData.name) {
        const clientResult = await createClient({
          name: clientData.name,
          contact_name: clientFormData.contact_name,
          abn: clientFormData.abn,
          channel_partner: clientFormData.channel_partner,
          sf_account_id: clientFormData.sf_account_id,
          email: clientFormData.email,
          phone: clientFormData.phone,
          industry: clientData.industry || "Energy",
        });

        if (!clientResult.success || !clientResult.data) {
          throw new Error(clientResult.error || "Failed to create client");
        }
        clientId = clientResult.data.id;
        setClientData({ ...clientData, id: clientId });
        setSelectedClientId(clientId);
      } else if (clientId && clientId !== 'new' && clientId !== 'none') {
        // Update existing client with any modified field values
        await updateClient(clientId, {
          name: clientData.name,
          contact_name: clientFormData.contact_name,
          abn: clientFormData.abn,
          channel_partner: clientFormData.channel_partner,
          sf_account_id: clientFormData.sf_account_id,
          email: clientFormData.email,
          phone: clientFormData.phone,
          industry: clientData.industry || "Energy",
        });
      }

      if (!clientId || clientId === 'new' || clientId === 'none') {
        toast({
          title: "Validation Error",
          description: "Please select or create a client.",
          variant: "destructive",
        });
        setIsSaving(false);
        return;
      }

      // 2. Create or Update Tender
      let currentTenderId = tenderId;
      if (!currentTenderId) {
        const tenderResult = await createTenderWorkflow(clientId, tenderName);
        if (!tenderResult.success) {
          throw new Error(tenderResult.error || "Failed to create tender");
        }
        currentTenderId = tenderResult.tenderId;

        // Flush pending SF import now that tender exists
        if (pendingSfImport && currentTenderId) {
          const storeResult = await salesforceImportService.storeImportRecord(
            currentTenderId,
            pendingSfImport.sfOpportunityId,
            pendingSfImport.sfTenderId,
            pendingSfImport.rawPayload,
            pendingSfImport.fieldMappingLog
          );
          if (!storeResult.success) {
            console.warn('⚠️ Could not save pending SF import record:', storeResult.error);
          }
          if (pendingSfImport.consultant) {
            await supabase
              .from('tender')
              .update({ extn: { consultant: pendingSfImport.consultant } })
              .eq('id', currentTenderId);
          }
          setSfImportExists(true);
          setSfImportedAt(new Date().toISOString());
          setPendingSfImport(null);
        }
      } else {
        await updateTender(currentTenderId, {
          opportunity_name: tenderName,
          client_id: clientId,
        });
      }

      // 3. Sync Sites (Global + Link)
      if (currentTenderId && orgId && sites.length > 0) {
        const updatedSites = await saveSitesToGlobalAndLink(sites, clientId, currentTenderId, orgId);
        setSites(updatedSites);
      }

      // 4. Save Workflow State
      await saveCurrentStepData(1, {
        clientData,
        sites: sites, // Note: using current sites, but ideally should use updatedSites if we could await state update
        tenderName,
        selectedClientId: clientId,
      }, false); // Not completed

      toast({
        title: "Draft Saved",
        description: "Proposal draft saved successfully.",
      });

    } catch (error: any) {
      console.error("Error saving draft:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to save draft",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Single Section - Client Information */}
      <Card className="border border-primary/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2 rounded-lg">
                <Building2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl font-semibold text-gray-900">
                  Client Information
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Select an existing client or create a new one for your tender
                </p>
              </div>
            </div>

            {/* Right side: SF import + demo button */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              {/* Salesforce Import Row */}
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Opportunity ID"
                  value={sfOpportunityIdInput}
                  onChange={(e) => { setSfOpportunityIdInput(e.target.value); if (e.target.value) setSfTenderIdInput(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleImportFromSalesforce(); }}
                  className="h-9 w-40 text-sm border-blue-200 focus:border-blue-400"
                />
                <span className="text-xs text-gray-400">or</span>
                <Input
                  placeholder="Tender ID"
                  value={sfTenderIdInput}
                  onChange={(e) => { setSfTenderIdInput(e.target.value); if (e.target.value) setSfOpportunityIdInput(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleImportFromSalesforce(); }}
                  className="h-9 w-40 text-sm border-blue-200 focus:border-blue-400"
                />
                <Button
                  onClick={() => handleImportFromSalesforce()}
                  disabled={isImporting || (!sfOpportunityIdInput.trim() && !sfTenderIdInput.trim())}
                  variant="outline"
                  size="sm"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:border-blue-400 whitespace-nowrap"
                >
                  {isImporting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
                  ) : (
                    <><Cloud className="w-4 h-4 mr-2" />Import from Salesforce</>
                  )}
                </Button>
                {sfImportExists && sfImportedAt && (
                  <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-xs font-normal whitespace-nowrap">
                    Imported {new Date(sfImportedAt).toLocaleDateString()}
                  </Badge>
                )}
              </div>

            </div>
          </div>

          {/* Re-import confirmation dialog */}
          {showReimportDialog && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800">Already imported from Salesforce</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  This tender was already imported on {sfImportedAt ? new Date(sfImportedAt).toLocaleString() : 'a previous session'}.
                  Re-importing will overwrite current field values.
                </p>
                <div className="flex gap-2 mt-2">
                  <Button size="sm" variant="outline" className="h-7 text-xs border-amber-300 text-amber-700" onClick={() => setShowReimportDialog(false)}>Cancel</Button>
                  <Button size="sm" className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleImportFromSalesforce(true)}>Re-import</Button>
                </div>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="space-y-6">

          {/* Tender/Proposal Name */}
          <div className="space-y-2">
            <Label htmlFor="tender-name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary" />
              Proposal Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="tender-name"
              placeholder="Enter proposal/tender name (e.g., ABC Corp Energy Tender 2024)"
              value={tenderName}
              onChange={(e) => {
                const value = e.target.value;
                setTenderName(value);
              }}
              className="h-12 text-base border-gray-200 focus:border-primary focus:ring-primary/20"
            />
            <p className="text-xs text-muted-foreground">
              This name will be used to identify this proposal in your system
            </p>
          </div>

          {/* Client Selector */}
          <div className="space-y-2">
            <Label className="text-sm font-medium text-gray-700">
              Select Client
            </Label>
            <Popover open={clientComboOpen} onOpenChange={setClientComboOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={clientComboOpen}
                  className="w-full h-12 justify-between text-base border-gray-200 font-normal hover:bg-transparent"
                >
                  {selectedClientId === 'new' ? (
                    <span className="flex items-center gap-2 text-primary font-medium">
                      <User className="w-4 h-4" />
                      Create New Client
                    </span>
                  ) : selectedClientId && selectedClientId !== 'none' ? (
                    <span className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-muted-foreground" />
                      {clients.find(c => c.id === selectedClientId)?.name ?? 'Select a client'}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">Choose an existing client or create new</span>
                  )}
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="p-0" style={{ width: 'var(--radix-popover-trigger-width)' }} align="start">
                <Command shouldFilter={false}>
                  <CommandInput
                    placeholder="Search clients..."
                    value={clientSearch}
                    onValueChange={setClientSearch}
                  />
                  <CommandList>
                    <CommandEmpty>No clients found.</CommandEmpty>
                    <CommandGroup>
                      <CommandItem
                        value="none"
                        onSelect={() => { handleClientSelect('none'); setClientComboOpen(false); setClientSearch(''); }}
                        className="text-muted-foreground"
                      >
                        -- Select a client --
                      </CommandItem>
                      {can('client.create') && (
                        <CommandItem
                          value="new"
                          onSelect={() => { handleClientSelect('new'); setClientComboOpen(false); setClientSearch(''); }}
                          className="text-primary font-medium"
                        >
                          <User className="mr-2 h-4 w-4" />
                          Create New Client
                        </CommandItem>
                      )}
                    </CommandGroup>
                    {clients.filter(c => {
                      const q = clientSearch.toLowerCase();
                      return !q || c.name.toLowerCase().includes(q) || (c.abn ?? '').toLowerCase().includes(q);
                    }).length > 0 && (
                      <>
                        <CommandSeparator />
                        <CommandGroup>
                          {clients
                            .filter(c => {
                              const q = clientSearch.toLowerCase();
                              return !q || c.name.toLowerCase().includes(q) || (c.abn ?? '').toLowerCase().includes(q);
                            })
                            .map(client => (
                              <CommandItem
                                key={client.id}
                                value={client.id}
                                onSelect={() => { handleClientSelect(client.id); setClientComboOpen(false); setClientSearch(''); }}
                              >
                                <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                                {client.name}
                                {client.abn && (
                                  <span className="text-xs text-muted-foreground ml-2">(ABN: {client.abn})</span>
                                )}
                                {selectedClientId === client.id && (
                                  <Check className="ml-auto h-4 w-4 text-primary" />
                                )}
                              </CommandItem>
                            ))}
                        </CommandGroup>
                      </>
                    )}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* Show form fields only when client is selected or creating new */}
          {(selectedClientId && selectedClientId !== "none") && (
            <div className="space-y-5 pt-6 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 bg-primary rounded-full"></div>
                <h3 className="text-lg font-medium text-gray-900">
                  Client Details
                </h3>
                {selectedClientId && selectedClientId !== "new" && selectedClientId !== "none" && (
                  <Badge variant="outline" className="ml-auto">
                    <Check className="w-3 h-3 mr-1" />
                    Existing Client
                  </Badge>
                )}
                {(selectedClientId === "new" || isNewClient) && (
                  <Badge variant="outline" className="ml-auto bg-primary/5 text-primary border-primary/20">
                    <User className="w-3 h-3 mr-1" />
                    New Client
                  </Badge>
                )}
              </div>

              {/* Form Fields Grid - Full Width Responsive */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {/* Client Name */}
                <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
                  <Label htmlFor="client-name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-primary" />
                    Client Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="client-name"
                    placeholder="Enter client company name"
                    value={clientData.name}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    disabled={!isNewClient && selectedClientId !== ""}
                  />
                </div>

                {/* Contact Name */}
                <div className="sm:col-span-2 lg:col-span-2 xl:col-span-2">
                  <Label htmlFor="contact-name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Primary Contact Name
                  </Label>
                  <Input
                    id="contact-name"
                    placeholder="e.g., John Smith"
                    value={clientFormData.contact_name}
                    onChange={(e) => updateClientFormData("contact_name", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    disabled={!isNewClient && selectedClientId !== "new"}
                  />
                </div>

                {/* ABN with Validation */}
                <div>
                  <Label htmlFor="abn" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    ABN {!hasAcnShape(clientFormData.acn) && <span className="text-red-500">*</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      id="abn"
                      placeholder="11-digit ABN"
                      value={clientFormData.abn}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, ''); // Only allow digits
                        updateClientFormData("abn", value);
                      }}
                      className={`h-12 text-base mt-2 pr-10 transition-colors ${abnValidation.isValid === true
                        ? 'border-green-500 focus:border-green-600 focus:ring-green-200 bg-green-50'
                        : abnValidation.isValid === false
                          ? 'border-red-500 focus:border-red-600 focus:ring-red-200 bg-red-50'
                          : 'border-gray-200 focus:border-primary focus:ring-primary/20'
                        }`}
                      maxLength={11}
                    />
                    {abnValidation.isValidating && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                      </div>
                    )}
                    {abnValidation.isValid === true && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    {abnValidation.isValid === false && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 mt-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-white text-xs font-bold">✗</span>
                      </div>
                    )}
                  </div>
                  {abnValidation.message && (
                    <p className={`text-sm mt-2 font-medium flex items-center gap-2 ${abnValidation.isValid === true
                      ? 'text-green-700'
                      : abnValidation.isValid === false
                        ? 'text-red-700'
                        : 'text-gray-600'
                      }`}>
                      {abnValidation.isValid === true && <span className="text-green-500">✓</span>}
                      {abnValidation.isValid === false && <span className="text-red-500">✗</span>}
                      {abnValidation.message}
                    </p>
                  )}
                  {!abnValidation.message && (
                    <p className="text-xs text-gray-500 mt-1">
                      Australian Business Number (11 digits)
                      {hasAcnShape(clientFormData.acn) && ' — optional, an ACN has been given'}
                    </p>
                  )}
                </div>

                {/*
                  ACN — the alternative identifier.

                  ⚠️ Entities exist with an ACN and no ABN. Without this field the only way past
                  Step 1 was to invent an ABN, which then printed on the client's proposal. See
                  lib/companyIdentifier.ts.
                */}
                <div>
                  <Label htmlFor="acn" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    ACN {!hasAbnShape(clientFormData.abn) && <span className="text-red-500">*</span>}
                  </Label>
                  <Input
                    id="acn"
                    placeholder="9-digit ACN"
                    value={clientFormData.acn}
                    onChange={(e) => updateClientFormData("acn", e.target.value.replace(/\D/g, ''))}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    maxLength={9}
                    disabled={!isNewClient && selectedClientId !== "new"}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Australian Company Number (9 digits). Required only when there is no ABN.
                  </p>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="contact@company.com.au"
                    value={clientFormData.email}
                    onChange={(e) => updateClientFormData("email", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Phone
                  </Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+61 2 9876 5432"
                    value={clientFormData.phone}
                    onChange={(e) => updateClientFormData("phone", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                  />
                </div>

                {/* Channel Partner */}
                <div>
                  <Label htmlFor="channel-partner" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Channel Partner
                  </Label>
                  <Input
                    id="channel-partner"
                    placeholder="Partner name (optional)"
                    value={clientFormData.channel_partner}
                    onChange={(e) => updateClientFormData("channel_partner", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                  />
                </div>

                {/* Salesforce Account ID */}
                <div>
                  <Label htmlFor="sf-account-id" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    SF Account ID
                  </Label>
                  <Input
                    id="sf-account-id"
                    placeholder="Salesforce Account ID"
                    value={clientFormData.sf_account_id}
                    onChange={(e) => updateClientFormData("sf_account_id", e.target.value)}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                  />
                </div>
              </div>

              {/* Save Client Button - Only show for new clients */}
              {(selectedClientId === "new" || isNewClient) && (
                <div className="pt-4 border-t border-gray-100">
                  {/* name is optional-chained below: restored workflow_data.step1.clientData
                      is only truthiness-checked, so {} passes and would throw here in render */}
                  <Button
                    onClick={handleSaveClient}
                    disabled={!clientData.name?.trim() || isSaving}
                    className="w-full sm:w-auto"
                  >
                    {isSaving ? "Saving Client..." : "Save Client"}
                  </Button>
                  <p className="text-xs text-gray-500 mt-2">
                    Save this client to your CRM for future use
                  </p>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/*
        F9 — "Prepared for:" on the proposal cover.

        Their words: "If multi-site has multiple entity names, we want to make the
        'Prepared for:' customizable." A tender covering several legal entities has no single
        Client record that is the truthful answer, so the broker states it.

        Optional by design: left empty, the cover prints the client's name exactly as it always
        has, which is every tender that has not been overridden.
      */}
      <Card className="border border-primary/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full" />
            <CardTitle className="text-xl font-semibold text-gray-900">Proposal Cover</CardTitle>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Who the proposal is addressed to. Leave blank to use the client's name.
          </p>
        </CardHeader>
        <CardContent>
          <Label htmlFor="prepared-for" className="text-sm font-medium text-gray-700">
            Prepared for
          </Label>
          <Input
            id="prepared-for"
            value={preparedFor}
            onChange={(e) => setPreparedFor(e.target.value)}
            placeholder={clientData?.name || "Client name"}
            className="mt-2"
          />
          <p className="text-xs text-gray-500 mt-2">
            Useful when the tender covers several entities — for example
            "H.A. Bachrach (Nom.) Pty Ltd &amp; The North Coast Gateway Centre Pty Ltd".
          </p>
        </CardContent>
      </Card>

      {/*
        Broker Details — the consultant printed on the proposal's last page.

        ⚠️ MANDATORY ON PURPOSE. Generation has no second source for these.

        It used to resolve them from the Salesforce tender owner, falling back to whoever
        clicked Generate. Measured on prod: 272 of 899 tenders have no Salesforce consultant,
        and every base_profiles row has an email but no phone — so on 40% of tenders the phone
        could not be filled from anywhere and {consultant_mobile} printed as a raw token in
        front of the client. Asked once here, it is fixed to the tender.
      */}
      <Card className="border border-primary/10 shadow-lg hover:shadow-xl transition-all duration-300 bg-white">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-primary rounded-full" />
            <CardTitle className="text-xl font-semibold text-gray-900">Broker Details</CardTitle>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Printed on the proposal so the client knows who to contact.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {consultantLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading your details…
            </div>
          ) : (
            <>
              {consultantPrefillNote && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>{consultantPrefillNote}</span>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div>
                  <Label htmlFor="consultant-name" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4 text-primary" />
                    Broker Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="consultant-name"
                    placeholder="Jane Smith"
                    value={consultant.name}
                    onChange={(e) => {
                      consultantEditedRef.current = true;
                      setConsultant((prev) => ({ ...prev, name: e.target.value }));
                    }}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    aria-invalid={showSiteErrors && !!consultantErrors.name}
                    required
                  />
                  {showSiteErrors && consultantErrors.name && (
                    <p className="text-xs text-red-500 mt-1">{consultantErrors.name}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="consultant-email" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    Broker Email <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="consultant-email"
                    type="email"
                    placeholder="jane.smith@choiceenergy.com.au"
                    value={consultant.email}
                    onChange={(e) => {
                      consultantEditedRef.current = true;
                      setConsultant((prev) => ({ ...prev, email: e.target.value }));
                    }}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    aria-invalid={showSiteErrors && !!consultantErrors.email}
                    required
                  />
                  {showSiteErrors && consultantErrors.email && (
                    <p className="text-xs text-red-500 mt-1">{consultantErrors.email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="consultant-phone" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                    <Phone className="w-4 h-4 text-primary" />
                    Broker Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="consultant-phone"
                    type="tel"
                    placeholder="0400 000 000"
                    value={consultant.phone}
                    onChange={(e) => {
                      consultantEditedRef.current = true;
                      setConsultant((prev) => ({ ...prev, phone: e.target.value }));
                    }}
                    className="h-12 text-base mt-2 border-gray-200 focus:border-primary focus:ring-primary/20"
                    aria-invalid={showSiteErrors && !!consultantErrors.phone}
                    required
                  />
                  {showSiteErrors && consultantErrors.phone && (
                    <p className="text-xs text-red-500 mt-1">{consultantErrors.phone}</p>
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Sites Management Section */}
      <SitesManagementSection
        sites={sites}
        onSitesChange={setSites}
        showErrors={showSiteErrors}
        /* Removing a site has to delete its tender_site row, which needs the tender. */
        tenderId={tenderId}
        /**
         * ⚠️ A MULTI-SITE TENDER IMPORTS FROM EACH SITE CARD, NOT FROM THE TOP.
         *
         * Those imports carry the same Salesforce tender owner, but only the top-level import
         * used to seed Broker Details — so a multi-site tender kept the signed-in user's profile
         * pre-fill and the proposal printed their contact details as the broker's. Reported as
         * "the portal is grabbing my contact details instead of broker's ... only multis maybe?".
         *
         * Same merge rule as the top-level import: the owner replaces a pre-fill, never
         * something this person typed.
         */
        onSalesforceConsultant={(sfConsultant) => {
          if (!sfConsultant) return;
          const merged = mergeSalesforceConsultant(consultant, sfConsultant, consultantEditedRef.current);
          setConsultant(merged);
          setConsultantPrefillNote(
            merged.phone
              ? null
              : "Broker details filled from the Salesforce tender owner. Add their contact number — it prints on the proposal, so do not use the client's.",
          );
        }}
      />

      {/* Workflow Navigation - Mobile Responsive */}
      <div className="pt-6">
        <WorkflowNavigation
          onNext={handleNext}
          showBackButton={false}
          isNextDisabled={isSaving}
          nextButtonText={
            isSaving
              ? isNewClient && clientData.name
                ? "Creating Client & Saving Sites..."
                : "Saving Sites..."
              : "Continue to Offers"
          }
          customAction={
            <Button
              variant="outline"
              onClick={handleSaveDraft}
              disabled={isSaving}
              className="gap-2"
            >
              <Save className="w-4 h-4" />
              Save Draft
            </Button>
          }
        />
      </div>
    </div>
  );
};

export default ClientInfoStep;
